<template>
    <div class="measure-container">
        <div :id="'measure_' + partIndex + '_' + measureIndex" class="measure" :class="{ editable: isEditable }"
             @mouseover="onMouseOver()" @mouseout="onMouseOut()" :style="{ height: height + 'px' }">
        </div>
        <div v-if="song.displayMeasureNumber && partIndex !== -1" class="measure-number"
             :style="{ left: (this.measure.openRepeat) ? '6px' : '1px', backgroundColor: bgcolor }">
            {{ measureNumber }}
        </div>
        <div class="measure-repeats" v-if="measure.numberRepeats > 2">x {{ measure.numberRepeats }}</div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SVG from 'svg.js';
import ChordModel from '@/models/Chord';
import MeasureModel from '@/models/Measure';

const margin      = 1;
const strokeWidth = 1;

let draw, group, defaultWidth, defaultHeight;

export default {
    name:  'measure',
    props: {
        partIndex:    Number,
        measureIndex: Number,
        columnsMax:   Number,
        width:        Number,
        height:       Number,
        isEditable:   Boolean,
        measure:      {
            type: Object,
        },
    },
    data: function()
    {
        return {
            onOver: false,
        };
    },
    mounted: function()
    {
        this.initCanvas();
    },
    watch: {
        measure: function()
        {
            // Lorsque la mesure change, on redessine la mesure
            // ------------------------------------------------
            this.redraw();
        },
    },
    computed: {
        ...mapGetters({
            song: 'song',
        }),
        // Nombre d'accords à afficher en comptant les éléments null
        totalChordsNumber: function()
        {
            return (this.measure && this.measure.chords) ? this.measure.chords.length : 0;
        },
        // Nombre d'accords à afficher en excluant les éléments null
        totalNotNullChordsNumber: function()
        {
            return MeasureModel.getTotalNotEmptyChordsNumber(this.measure);
        },
        // Index global
        index: function()
        {
            return this.partIndex + '_' + this.measureIndex;
        },
        // Numéro de la mesure
        measureNumber: function()
        {
            return MeasureModel.getMeasureNumber(this.song, this.partIndex, this.measureIndex);
        },
        // Couleur de fond de la mesure
        bgcolor: function()
        {
            return (this.measure.color) ? this.measure.color : '#FFFFFF';
        },
        // Hauteur de la zone contenant le mesure et ses informations supplémentaires
        zoneHeight: function()
        {
            let height = this.height;

            if (this.measure.numberRepeats > 2)
            {
                height += 26;
            }
            else if (this.song.displayMeasureNumber)
            {
                height += 16;
            }

            return height;
        },
    },
    methods: {
        /**
         * On redessine la mesure
         *
         * @author Fabien Bellanger
         */
        redraw: function()
        {
            // Lorsque la mesure change, on vide le contenu du conteneur et on redessine la mesure
            // -----------------------------------------------------------------------------------
            const element = document.getElementById('measure_' + this.partIndex + '_' + this.measureIndex);

            if (element !== null)
            {
                while (element.firstChild)
                {
                    element.removeChild(element.firstChild);
                }
            }

            this.initCanvas();
        },
        /**
         * Initialisation de la mesure
         *
         * @author Fabien Bellanger
         */
        initCanvas: function()
        {
            defaultWidth = this.width - margin;
            defaultHeight = this.height - margin;

            // On dessine la mesure
            // --------------------
            this.drawRect();

            if (MeasureModel.isEmptyMeasure(this.measure))
            {
                this.drawRepeats();
            }
            else if (!MeasureModel.isValid(this.measure))
            {
                this.drawNotValid();
            }
            else
            {
                switch (this.totalChordsNumber)
                {
                    case 1:
                        this.drawOneChord();
                        break;
                    case 2:
                        this.drawTwoChords();
                        break;
                    case 4:
                        if (this.totalNotNullChordsNumber === 1)
                        {
                            this.drawOneChord();
                        }
                        else if (this.totalNotNullChordsNumber === 2)
                        {
                            this.drawTwoChords();
                        }
                        else if (this.totalNotNullChordsNumber === 3)
                        {
                            if (this.measure.chords[2].root === '' || this.measure.chords[0].root === '')
                            {
                                this.drawFourChords();
                            }
                            else
                            {
                                this.drawThreeChords();
                            }
                        }
                        else if (this.totalNotNullChordsNumber === 4)
                        {
                            this.drawFourChords();
                        }

                        break;
                }
                
                this.drawRepeats();
            }
        },
        /**
         * Affichage des barres de répétition
         *
         * @author Fabien Bellanger
         */
        drawRepeats: function()
        {
            // Affichage des barres de répétition
            // ----------------------------------
            if (this.measure.openRepeat)
            {
                MeasureModel.drawOpenRepeat(draw, group, defaultHeight, strokeWidth);
            }

            if (this.measure.closeRepeat)
            {
                MeasureModel.drawCloseRepeat(draw, group, defaultWidth, defaultHeight, strokeWidth);
            }
        },
        /**
         * Affichage du rectangle
         *
         * @author Fabien Bellanger
         */
        drawRect: function()
        {
            // Initialisation de l'objet SVG
            if (document.getElementById('measure_' + this.index))
            {
                draw = SVG('measure_' + this.index).size(defaultWidth + margin, defaultHeight + margin);
                group = draw.group();

                // Rectangle
                const rect = draw.rect(defaultWidth, defaultHeight)
                    .fill(this.bgcolor)
                    .stroke({ width: strokeWidth })
                    .move(margin / 2, margin / 2);
                group.add(rect);

                // const line1 = draw.line(defaultWidth / 2, 0, defaultWidth / 2, defaultHeight)
                //     .stroke({ width: strokeWidth, color: 'blue' });
                // group.add(line1);

                // const line2 = draw.line(0, defaultHeight / 2, defaultWidth, defaultHeight / 2)
                //     .stroke({ width: strokeWidth, color: 'blue' });
                // group.add(line2);
            }
        },
        /**
         * Affichage d'une mesure non valide
         *
         * @author Fabien Bellanger
         */
        drawNotValid: function()
        {
            // Affichage d'un message d'erreur
            // -------------------------------
            const message = draw.text(this.$t('measure-not-valid')).move(defaultWidth / 2, defaultHeight / 2 - 8);
            message.font({ anchor: 'middle', size: 16, family: 'Roboto', fill: 'black' });
            group.add(message);
        },
        /**
         * Mesure avec 1 accord
         *
         * @author Fabien Bellanger
         */
        drawOneChord: function()
        {
            if (this.measure.chords[0].root !== '')
            {
                ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[0]), '', 1, defaultWidth, defaultHeight);
            }
        },
        /**
         * Affichage d'une mesure avec 2 accords
         *
         * @author Fabien Bellanger
         */
        drawTwoChords: function()
        {
            if (this.measure.chords[0].root !== null && this.measure.chords[0].root !== '')
            {
                let line2;
                
                // Première diagonale
                // ------------------
                const line1 = draw.line(defaultWidth, 0, 0, defaultHeight)
                    .stroke({ width: strokeWidth })
                    .move(margin / 2, margin / 2);
                group.add(line1);

                // Accords
                // -------
                if (this.measure.chords[2].root !== null && this.measure.chords[2].root !== '')
                {
                    // 1er et 3ème temps
                    // -----------------
                    ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[0]), 'LT', 2, defaultWidth, defaultHeight);
                    ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[2]), 'BR', 2, defaultWidth, defaultHeight);
                }
                else if (this.measure.chords[1].root !== null && this.measure.chords[1].root !== '')
                {
                    // 1er et 2ème temps
                    // -----------------

                    // Seconde diagonale
                    // -----------------
                    line2 = draw.line(0, 0, defaultWidth / 2, defaultHeight / 2)
                        .stroke({ width: strokeWidth })
                        .move(margin / 2, margin / 2);
                    group.add(line2);

                    ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[0]), 'L', 4, defaultWidth, defaultHeight);
                    ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[1]), 'T', 4, defaultWidth, defaultHeight);
                }
                else if (this.measure.chords[3].root !== null && this.measure.chords[3].root !== '')
                {
                    // 1er et 4ème temps
                    // -----------------

                    // Seconde diagonale
                    // -----------------
                    line2 = draw.line(defaultWidth / 2, defaultHeight / 2, defaultWidth, defaultHeight)
                        .stroke({ width: strokeWidth })
                        .move((defaultWidth + margin) / 2, (defaultHeight + margin) / 2);
                    group.add(line2);

                    ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[0]), 'LT', 2, defaultWidth, defaultHeight);
                    ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[3]), 'B', 4, defaultWidth, defaultHeight);
                }
            }
        },
        /**
         * Affichage d'une mesure avec 3 accords
         *
         * @author Fabien Bellanger
         */
        drawThreeChords: function()
        {
            if (!ChordModel.isEmpty(this.measure.chords[1]) || !ChordModel.isEmpty(this.measure.chords[3]))
            {
                let line2;

                // Première diagonale
                // ------------------
                const line1 = draw.line(defaultWidth, 0, 0, defaultHeight)
                    .stroke({ width: strokeWidth })
                    .move(margin / 2, margin / 2);
                group.add(line1);

                // Accords
                // -------
                if (ChordModel.isEmpty(this.measure.chords[1]))
                {
                    // La zone au haut à gauche est entière
                    // ------------------------------------

                    // Seconde diagonale
                    // -----------------
                    line2 = draw.line(defaultWidth / 2, defaultHeight / 2, defaultWidth, defaultHeight)
                        .stroke({ width: strokeWidth })
                        .move((defaultWidth + margin) / 2, (defaultHeight + margin) / 2);
                    group.add(line2);

                    // Accords
                    // -------
                    ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[0]), 'LT', 2, defaultWidth, defaultHeight);
                    ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[2]), 'R', 4, defaultWidth, defaultHeight);
                    ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[3]), 'B', 4, defaultWidth, defaultHeight);
                }
                else if (ChordModel.isEmpty(this.measure.chords[3]))
                {
                    // La zone en bas à droite est entière
                    // -----------------------------------

                    // Seconde diagonale
                    // -----------------
                    line2 = draw.line(0, 0, defaultWidth / 2, defaultHeight / 2)
                        .stroke({ width: strokeWidth })
                        .move(margin / 2, margin / 2);
                    group.add(line2);

                    // Accords
                    // -------
                    ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[0]), 'L', 4, defaultWidth, defaultHeight);
                    ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[1]), 'T', 4, defaultWidth, defaultHeight);
                    ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[2]), 'BR', 2, defaultWidth, defaultHeight);
                }
            }
        },
        /**
         * Affichage d'une mesure avec 4 accords
         *
         * @author Fabien Bellanger
         */
        drawFourChords: function()
        {
            // Diagonales
            // ----------
            const line1 = draw.line(0, 0, defaultWidth, defaultHeight)
                .stroke({ width: strokeWidth })
                .move(margin / 2, margin / 2);
            group.add(line1);
            const line2 = draw.line(defaultWidth, 0, 0, defaultHeight)
                .stroke({ width: strokeWidth })
                .move(margin / 2, margin / 2);
            group.add(line2);

            // Accords
            // -------
            ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[0]), 'L', 4, defaultWidth, defaultHeight);
            ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[1]), 'T', 4, defaultWidth, defaultHeight);
            ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[2]), 'R', 4, defaultWidth, defaultHeight);
            ChordModel.drawChord(draw, group, ChordModel.getName(this.measure.chords[3]), 'B', 4, defaultWidth, defaultHeight);
        },
        /**
         * Survole de la mesure
         *
         * @author Fabien Bellanger
         */
        onMouseOver: function()
        {
            this.onOver = true;
        },
        /**
         * Quitte la mesure
         *
         * @author Fabien Bellanger
         */
        onMouseOut: function()
        {
            this.onOver = false;
        },
    },
};
</script>

<style scoped lang="scss">
    .measure-container {
        margin:   -0.5px;
        padding:  0px;
        position: relative;

        .measure-number {
            position:         absolute;
            top:              1px;
            left:             6px;
            line-height:      10px;
            padding:          0px 1px;
            text-align:       left;
            font-size:        9px;
            color:            #444;
            background-color: white;
        }

        .measure-repeats {
            position:         absolute;
            top:              1px;
            right:            2px;
            line-height:      13px;
            padding:          0px 2px;
            border-left:      1px solid #333;
            border-bottom:    1px solid #333;
            font-size:        12px;
            font-weight:      500;
            text-align:       right;
            background-color: white;
            color:            #333;
        }
        
        .measure {
            position:    relative;
            font-family: 'Roboto';

            .measure-over {
                position:         absolute;
                display:          flex;
                top:              0;
                left:             0;
                width:            100%;
                height:           100%;
                z-index:          1000;
                justify-content:  center;
                align-items:      center;
                background-color: rgba(100, 100, 100, 0.9);
            }
        }

        .editable {
            cursor: pointer;
        }
    }
</style>
